import {Injectable} from '@angular/core';
import {isNil} from 'lodash';
import {BehaviorSubject, Observable} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {tap} from 'rxjs/operators';
import {ROLE, User, UserPermission} from '../../model/user';
import {Router} from '@angular/router';
import { Permission } from 'src/app/views/wrapper/payroll/company/setting/settings';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  userSubject = new BehaviorSubject<User>(null);
  private isLoading = false;
  currentUser: User;
  resetGetAuthProfile = false;

  constructor(
    private http: HttpClient,
    private router: Router) {
  }

  resetPassword(body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/auth/password/reset`, {
      ...body
    });
  }

  forgotPassword(body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/auth/password/request`, {
      ...body
    });
  }


  refreshToken(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/auth/refresh`, {
      headers: {
        Authorization: `Bearer ${this.getRefreshToken()}`
      }
    }).pipe(
      tap((result) => this.setSession(result))
    );
  }

  login(body: any): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/auth/login`, {
      ...body
    }).pipe(
      tap((result) => {
        this.setSession(result);
        this.currentUser = result.profile;
      })
    );
  }

  logout(): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/auth/logout`, {}, {
      headers: {
        Authorization: `Bearer ${this.getAccessToken()}`
      }
    }).pipe(
      tap(() => this.removeSession())
    );
  }

  getAuthProfile(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/auth/profile`, {
      headers: {
        Authorization: `Bearer ${this.getAccessToken()}`
      }
    });
  }

  getAccessToken(): string {
    return localStorage.getItem('access_token');
  }

  getRefreshToken(): string {
    return localStorage.getItem('refresh_token');
  }

  isAccessTokenExpired(): boolean {
    const expireTime = parseInt(localStorage.getItem('expire_time'), 10);
    const now = new Date().getTime();
    return expireTime - now < (30 * 1000);
  }

  isAuthenticated(): boolean {
    return !isNil(this.getAccessToken()) && !this.isAccessTokenExpired();
  }

  setSession({access_token, refresh_token, exp}: { access_token: string, refresh_token: string, exp: number }) {
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('expire_time', JSON.stringify(exp.valueOf()));
  }

  removeSession() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('expire_time');
  }

  get userObs(): Observable<User> {
    return this.userSubject.asObservable();
  }

  getCurrentUser(): BehaviorSubject<User> {
    if (this.currentUser && !this.resetGetAuthProfile) {
      this.userSubject.next(this.currentUser);
    } else if (!this.isLoading || this.resetGetAuthProfile) {
      this.isLoading = true;
      this.getAuthProfile().subscribe((profile) => {
          this.currentUser = {...profile.data, time: new Date().getTime()};
          this.isLoading = false;
          this.userSubject.next(this.currentUser);
        },
        (err) => {
          this.isLoading = false;
          this.userSubject.next(null);
          console.error(err);
        });
    }
    return this.userSubject;
  }

  isPigasus(): Observable<boolean> {
    return new Observable(observable => {
      this.getCurrentUser().subscribe((user: User) => {
        if (user) {
          observable.next(user?.role === ROLE.PIGASUS);
          observable.complete();
        }
      });
    });
  }

  isAdmin(): Observable<boolean> {
    return new Observable(observable => {
      this.getCurrentUser().subscribe((user: User) => {
        if (user) {
          observable.next(user?.role === ROLE.ADMIN);
          observable.complete();
        }
      });
    });
  }

  getAdminListByCompany(companyId: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/auth/companies/${companyId}/admin`);
  }

  addAdmin(body): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/auth/create_admin`, body);
  }

  updateAdmin(body): Observable<any> {
    return this.http.put(`${environment.apiBaseUrl}/auth/update_admin`, body);
  }

  deleteUserPermission(userId: string, companyId: string): Observable<any> {
    return this.http.delete(`${environment.apiBaseUrl}/auth/delete_user_permissions/${userId}/${companyId}`);
  }

  deleteUser(userId: string): Observable<any> {
    return this.http.delete(`${environment.apiBaseUrl}/auth/delete_user/${userId}`);
  }

  redirectToLogin(): void {
    this.removeSession();
    if (this.router.url.indexOf('/login') === 0) {
      this.router.navigateByUrl(this.router.url);
      return
    }
    const keep = `?go=${encodeURIComponent(this.router.url)}`;
    this.router.navigateByUrl(`login${keep}`);
  }

  getUsers(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/auth/userlist`);
  }

  updateUsersType(body): Observable<any> {
    return this.http.patch(`${environment.apiBaseUrl}/auth/userlist`, body);
  }

  loginDbd(body): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/auth/login/dbd`, body).pipe(
      tap((result) => {
        this.setSession(result);
        this.currentUser = result.profile;
      })
    );
  }

  getUserPermission(companyId: string): Observable<UserPermission> {
    return new Observable(observable => {
      this.getAuthProfile().subscribe((resp: any) => {
        if (resp.success) {
          let user: User = resp.data
          let u = {
            email: user.email,
            role: user.role,
            permission_list: user.companies?.find(item => companyId === item.id)?.permission_list,
            isPigasus: user.role === ROLE.PIGASUS,
            isAdmin: user.role === ROLE.ADMIN,
          }
          observable.next(u)
          observable.complete()
        }
      })
    })
  }

  checkUserPermission(u: UserPermission, permission: Permission): boolean {
    if (u) {
      if (u.role === ROLE.ADMIN) {
        return u.permission_list && u.permission_list.includes(permission)
      } else {
        // Pigasus, Super Admin
        return true
      }
    } else {
      return false
    }
  }

}
